/**
 * CronologiaStatiList — F-006 Phase 5 (IMPL-P5-005).
 *
 * Semantic `<ol>` timeline of the 4 canonical pratica milestones:
 *   INVIATA → PRESA_IN_CARICO → IN_ELABORAZIONE → COMPLETATA
 *
 * Each milestone is rendered regardless of whether the API supplied a date.
 * Future milestones (not in the cronologia) are greyed out; completed ones
 * are highlighted green; the most recent (current) is emphasized.
 *
 * Dates use `<time datetime>` so screen readers and other tools can parse
 * them.
 */
import { Check, Circle } from 'lucide-react';
import type { CronologiaEntry } from '@app/shared';

const MILESTONES = [
  { key: 'INVIATA' as const, label: 'Inviata' },
  { key: 'PRESA_IN_CARICO' as const, label: 'Presa in carico' },
  { key: 'IN_ELABORAZIONE' as const, label: 'In elaborazione' },
  { key: 'COMPLETATA' as const, label: 'Completata' },
];

function formatDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

interface CronologiaStatiListProps {
  entries: CronologiaEntry[];
}

export function CronologiaStatiList({ entries }: CronologiaStatiListProps) {
  // Map milestone → entry for quick lookup.
  const byMilestone = new Map(entries.map((e) => [e.milestone, e]));

  // The "current" milestone is the latest one reached.
  const reachedCount = MILESTONES.filter((m) => byMilestone.has(m.key)).length;
  const currentIndex = Math.max(0, reachedCount - 1);

  return (
    <section className="space-y-2" aria-label="Cronologia stati pratica">
      <h2 className="text-lg font-semibold">Cronologia</h2>
      <ol className="relative space-y-4 border-l pl-6">
        {MILESTONES.map((m, idx) => {
          const entry = byMilestone.get(m.key);
          const reached = !!entry;
          const isCurrent = idx === currentIndex && reached;

          const dotClass = reached
            ? isCurrent
              ? 'bg-primary text-primary-foreground border-primary'
              : 'bg-emerald-100 text-emerald-700 border-emerald-300 dark:bg-emerald-900 dark:text-emerald-200 dark:border-emerald-700'
            : 'bg-muted text-muted-foreground border-border';

          return (
            <li key={m.key} className="relative">
              <span
                aria-hidden="true"
                className={[
                  'absolute -left-[34px] inline-flex h-6 w-6 items-center justify-center rounded-full border',
                  dotClass,
                ].join(' ')}
              >
                {reached ? <Check className="h-3 w-3" /> : <Circle className="h-3 w-3" />}
              </span>
              <div className="space-y-0.5">
                <div
                  className={[
                    'text-sm font-medium',
                    reached ? 'text-foreground' : 'text-muted-foreground',
                    isCurrent ? 'font-semibold' : '',
                  ].join(' ')}
                >
                  {m.label}
                </div>
                <div className="text-xs text-muted-foreground">
                  {entry ? (
                    <time dateTime={entry.at}>{formatDateTime(entry.at)}</time>
                  ) : (
                    <span>In attesa</span>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
